import { FsAudio, FsExternalFile } from '@/filestore';
import { ILoadedMediaTypes, IPreviewBtnStatus, IPublishBtnStatus, ISceneSnapshotDict } from '@/store/actions';
import { IBounds } from '@/utils/transforms';
import {
	IActionCategory,
	IButton,
	IChromaKey,
	IComponentType,
	IFaceLandmark,
	IImage,
	IModel3d,
	IModel3dAnimationOptions,
	IText,
	IText3d,
	ITriggerTypes,
	ITuple2,
	ITuple3,
	ITuple4,
	IUserData,
	IVector3,
	IVector3Dict,
	IVector4,
	IVector4Dict,
	IVideo,
} from '@r3f-component-data-structure';
import { IVideoProgressDict } from '@r3f-component-data-structure/types/videoStatus';
import { IWebEmbedPreviewSettings } from '@r3f-component-data-structure/types/webEmbed';
import { IFileType, IProgressStatus } from '@zappar/zw-api-client';
import { ICanvasName, IEntityDrawerTypes, IEntityShelves, IOpenEntityCategoryDrawerDict } from '.';

export enum IToastType {
	error = 'error',
	warning = 'warning',
	info = 'info',
	success = 'success',
}
export interface IBaseToast {
	type: IToastType;
	message: string;
	timeout?: number;
	size?: IToastSize;
	contrast?: IToastContrast;
	stackToasts?: boolean;
	id?: string;
	singleton?: boolean;
	closable: boolean;
}

export interface IToast extends IBaseToast {
	id: string;
}

export enum IToastSize {
	default = 'default',
	mini = 'mini',
}

export enum IToastContrast {
	undefined = 'undefined',
	default = 'default',
	high = 'high',
}

export enum ITransformControlsMode {
	translate = 'translate',
	rotate = 'rotate',
	scale = 'scale',
	curve = 'curve',
}

export enum IProjectShelves {
	backgroundSound = 'backgroundSound',
	arWebEmbed = 'arWebEmbed',
	targetNotSeen = 'targetNotSeen',
	analytics = 'analytics',
	shadows = 'shadows',
	splashScreen = 'splashScreen',
	grabNGo = 'grabNGo',
    imageTrackingSettings = 'imageTrackingSettings',
    worldTrackingSettings = 'worldTrackingSettings',
    screenRelativeSettings = 'screenRelativeSettings'
}

export enum ISceneShelves {
	properties = 'properties',
	transitions = 'transitions',
	photoUi = 'photoUi',
	screenSettings = 'screenSettings',
	sceneActions = 'sceneActions',
	content360 = 'content360',
	targetProperty = 'targetProperty',
}

interface IDragStateBase {
	id: string;
	type: IComponentType;
	clickOffset: ITuple2;
	dimensions: ITuple2;
}

export interface IDragStateMediaFile extends IDragStateBase {
	type: IComponentType.Image | IComponentType.Video | IComponentType.Model3d;
	mediaFile: FsExternalFile;
}

export interface IDragStateEntity extends IDragStateBase {
	type: IComponentType.Text | IComponentType.Text3d | IComponentType.Button;
	entity: IText | IText3d | IButton;
}

export type IDragState = IDragStateMediaFile | IDragStateEntity;

export enum IModal {
	device = 'device',
	template = 'template',
	templateOverwrite = 'templateOverwrite',
	avaturn = 'avaturn',
	avaturnHelp = 'avaturnHelp',
	mediaLibrary = 'mediaLibrary',
	imageTrainer = 'imageTrainer',
	particles = 'particles',
	particlesHelp = 'particlesHelp',
	imageTrackingPreview = 'imageTrackingPreview',
	chromaKeyEdit = 'chromaKeyEdit',
	chromaKeyHelp = 'chromaKeyHelp',
	videoProcessingWarning = 'videoProcessingWarning',
	customLaunchScreen = 'customLaunchScreen',
	projectSizeLimit = 'projectSizeLimit',
	appClips = 'appClips',
	appClipsNoHideCheckbox = 'appClipsNoHideCheckbox',
	targetImageModal = 'targetImageModal',
	whatsNew = 'whatsNew',
	sendFeedback = 'sendFeedback',
	sessionExpired = 'sessionExpired',
	authorizationDenied = 'authorizationDenied',
}

export interface ISelectedTemplateProject {
	id: string;
	title: string;
	slug?: string;
}

export interface IActivePresetAnimationState {
	entityId: string;
	state: 'play' | 'stop' | 'pause';
	actionIndex: number;
}

export type IActivePresetAnimationData = IActivePresetAnimationState | null;
export type IActiveEditingActionData = {
	entityId: string;
	triggerType: ITriggerTypes;
	actionIndex: number;
} | null;

export type ILoadedModel3d = IModel3d & { isPublic: boolean };
export type ILoadedVideo = IVideo & { isPublic: boolean };
export type ILoadedImage = IImage & { isPublic: boolean };

export type ILoadedComponentUnion = ILoadedModel3d | ILoadedVideo | ILoadedImage;

export interface IChromaKeyEditModalData {
	chromaKey?: IChromaKey;
	filestoreId?: string;
}

export interface ISpinnerData {
	coords: ITuple3;
	isScreenRelative: boolean;
}

// User State
export interface IUserState {
	activeEditingActionData: IActiveEditingActionData;
	popOutIdealTopPosition: number | null;
	isPresetAnimationActive: boolean;
	multipleEntitySelectBoundary?: IBounds | null;
	chromaKeyEditModalData: IChromaKeyEditModalData;
	selectedTemplateProject?: ISelectedTemplateProject;
	targetImageReferenceObjectPosition: number | null;
	mediaLibraryDisplay: IMediaLibraryDisplay;
	modalById: Partial<Record<IModal, boolean>>;
	isWebEmbedEditingMode: boolean;
	previewCodeSrc?: string;
	previewUrl?: string;
	webEmbed?: IWebEmbedPreviewSettings;
	userId?: string;
	fileVerifying?: boolean;
	toasts: IToast[];
	is3dMode: boolean;
	isScreenRelativeMode: boolean;
	transformControlsMode: ITransformControlsMode;
	transformControlsActive: boolean;
	socketConnected?: boolean;
	projectLoadingProgress: number;
	projectLoadingFailure: boolean;
	selectedEntityIds?: string[];
	lockedEntityIds?: string[];
	copiedEntityIds?: string[];
	activeSceneId: string | null;
	positioningIsActive: boolean;
	scaleHotspotIsEnabled: boolean;
	rotationIsActive: boolean;
	backgroundHotspotIsEnabled: boolean;
	zoomLevel: number;
	undoDisabled: boolean;
	redoDisabled: boolean;
	markerIndexPressed: number | null;
	hotKeys: string[];
	pointerOffset: IVector3;
	entityDragHotspotPosition: IVector3 | null;
	selectedObject2DPosition: IVector3 | null;
	selectionTopLeft2DPosition: IVector3 | null;
	selectionTopRight2DPosition: IVector3 | null;
	selectionBottomLeft2DPosition: IVector3 | null;
	selectionBottomRight2DPosition: IVector3 | null;
	rotationMarker2DPosition: IVector3 | null;
	entityDrag2DPosition: IVector3 | null;
	groupIsXInverted: boolean;
	groupIsYInverted: boolean;
	sceneEntityListIsVisible: boolean;
	sceneEntityListPosition: [number, number];
	currentSceneIndex: number;
	scrollSceneMenu: boolean;
	sceneSnapshots: ISceneSnapshotDict;
	sceneCarouselScrollLeft: number;
	sceneMenuIsLarge: boolean;
	sceneNameInputIsFocused: boolean;
	project: IProjectData;
	loggedInUsers: { [id: string]: IUserData };
	canvasLoaded: boolean;
	entityMenuDragActive: boolean;
	dragState: IDragState | null;
	loadedImages: ILoadedImage[] | null;
	loadedVideos: ILoadedVideo[] | null;
	loaded3dModels: ILoadedModel3d[] | null;
	loadedBackgroundContent: (IBackgroundContent & { statusUrl: string })[] | null;
	selectedAudio: FsAudio | null;
	loadingMedia: boolean;
	currentUploads: IUploadInfoDict;
	numberProcessedUploads: number; //needed to calculate overall upload process
	trackImgData: ITrkImgData | null;
	trkImgIsUploading: boolean;
	pureReactInCanvasHovered: boolean; // legacy
	editSceneTitle: boolean;
	openInspectorMenuEntityShelves: IEntityShelves[] | null;
	openInspectorMenuProjectShelves: IProjectShelves[] | null;
	openInspectorMenuSceneShelves: ISceneShelves[] | null;
	openEntityMenuDrawer: IEntityDrawerTypes | null;
	openEntityCategoryDrawerDict: IOpenEntityCategoryDrawerDict;
	screenRelativeDevice: IScreenRelativeDevice;
	screenRelativeDevicePreviousCustomDimensions?: IScreenRelativeDeviceDimensions;
	tempAnimateModelAction: ({ type: IActionCategory.animateModel } & IModel3dAnimationOptions) | null;
	visibleCanvases: Record<ICanvasName, boolean>;
	positionById: IVector3Dict | { [id: string]: null };
	scaleById: IVector3Dict | { [id: string]: null };
	rotationById: IVector3Dict | { [id: string]: null };
	emitterAreaScalesById: IVector3Dict | { [id: string]: null };
	emitterIsStaticById: { [id: string]: boolean };
	curvatureById: { [id: string]: null | number };
	fillRgbaById: { [key: string]: IVector4[] | null };
	fontRgbaById: IVector4Dict | { [id: string]: null };
	borderRgbaById: IVector4Dict | { [id: string]: null };
	projectSizeLimit: null | number;
	projectSize: null | number;
	activeFaceLandmark: IFaceLandmark | null;
	showFaceLandmarks: boolean;
	hoveredDropdownFacelandmark: IFaceLandmark | null;
	forceUpdateDragControlsTrigger: number;
	temporaryDragRotation: ITuple3 | null;
	videoProgressById?: IVideoProgressDict;
	showPublishPopup: boolean;
	publishTrigger: number;
	spinnerData: { [id: string]: ISpinnerData };
	isProjectDuplicating: boolean;
	showAppClipsModal: boolean | null;
	publishBtnStatus: IPublishBtnStatus;
	previewBtnStatus: IPreviewBtnStatus;
	appClipsConverted: boolean | null;
	updateTrigger: number;
	activePreviewTrackingImageIndex: number | null;
	whatsNewTrigger: number;
	isUserAuthorized: boolean;
	launchPageIconFilestoreId: string | null;
}

export interface IScreenRelativeDeviceDimensions {
	height: number;
	width: number;
}

export interface IScreenRelativeDevice extends IScreenRelativeDeviceDimensions {
	label: IScreenRelativeDeviceLabels;
}

export enum IScreenRelativeDevices {
    IPHONE15 = 'IPHONE15',
    PIXEL8a = 'PIXEL8a',
	GALAXYS7 = 'GALAXYS7',
    IPAD11PRO = 'IPAD11PRO',
	HORIZONTAL = 'HORIZONTAL',
	CUSTOM = 'CUSTOM',
}

export enum IScreenRelativeDeviceLabels {
    IPHONE15 = 'iPhone 15',
    PIXEL8a = 'Pixel 8a',
    GALAXYS7 = 'Galaxy S7',
    IPAD11PRO = 'iPad 11 Pro',
    HORIZONTAL = 'Horizontal',
    CUSTOM = 'Custom',
}

export interface IMediaLibraryDisplay {
	fileTypes: IFileType[]; // File types to include in the media library
	category?: IMediaCategory; // Default tab - if undefined then 'All'
	onSubmitCategory?: IMediaLibraryOnSubmitCategory;
}

// TODO - This should be imported from web-components
export enum IMediaCategory {
	Audio = 'Audio',
	Image = 'Image',
	Video = 'Video',
	Model3D = '3D',
	Content360 = '360 content',
}

export enum IMediaLibraryOnSubmitCategory {
	AddToScene = 'AddToScene',
	ReplaceEntity = 'ReplaceEntity',
	SelectChromaKeyVideo = 'SelectChromaKeyVideo',
	AddIconToLaunchPage = 'AddIconToLaunchPage',
}

export const mediaLibraryCategoryByComponentType = {
	[IComponentType.Image]: IMediaCategory.Image,
	[IComponentType.Video]: IMediaCategory.Video,
	[IComponentType.Model3d]: IMediaCategory.Model3D,
} as { [k in IComponentType]: IMediaCategory };

export interface IUploadInfoDict {
	[id: string]: IUploadInfo;
}

export type IUploadInfo = IUploadData | null;

interface IUploadData {
	fileName?: string;
	type?: IProgressStatus;
	perc?: number | null;
	dataURl?: string | ArrayBuffer;
	errorStatus?: number;
	errorText?: string;
	mediaType?: ILoadedMediaTypes;
}

export interface IAudio {
	filestoreId: string;
	name: string;
	status?: IAudioStatus;
}

export interface IBackgroundContent {
	id: string;
	type?: IFileType.Image360 | IFileType.Video360;
	url: string;
	name: string;
}

export enum IAudioStatus {
	playing = 'playing',
	paused = 'paused',
	loading = 'loading',
}

export interface IProjectData {
	id: string;
	title: string;
	accountId?: string;
	status?: 'published' | 'unpublished';
	createdAt?: string;
	createdBy?: string;
	maxTargetImages?: number;
	canUseLmsIntegration?: boolean;
	sizeLimit?: number;
}

export interface ITrkImgData {
	quality?: number;
	aspectRatio?: number;
	codes?: string[];
	compositeUrl?: string;
}

export enum ILaunchScreenIconSize {
	medium = 'Medium',
	large = 'Large',
}

export interface ICustomLaunchScreenFormData {
	iconFilestoreId?: string | null;
	iconSize: ILaunchScreenIconSize | string;
	title: string;
	buttonText: string;
	backgroundColorRGBA: ITuple4;
	elementColorRGBA: ITuple4;
}
